import { UsersIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import { Text } from '../../../components/ui/text.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import { api } from '../../../utils/api.ts';
import { getFullName } from '../../../utils/name.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import type { CreatableMultiSelectOption } from '../../shared/components/creatable-multi-select.tsx';
import { CreatableMultiSelect } from '../../shared/components/creatable-multi-select.tsx';
import type { EventFormData } from './event-form-fields';

type EventAttendeesFieldProps = {
    control: UseFormReturn<EventFormData>['control'];
    setValue: UseFormReturn<EventFormData>['setValue'];
    disabled?: boolean;
};

export const EventAttendeesField = ({
    control,
    setValue,
    disabled,
}: EventAttendeesFieldProps): React.JSX.Element => {
    const { t } = useLocale('events');
    const [emailError, setEmailError] = useState('');
    const watchedAttendees = useWatch({ control, name: 'attendees' });
    const attendees = useMemo(() => watchedAttendees ?? [], [watchedAttendees]);

    const { data: platoUsers } = api.platoUser.query.users.getManyUsers.getList.useQuery();

    const options: CreatableMultiSelectOption[] = useMemo(() => {
        if (!platoUsers) {
            return [];
        }
        return platoUsers
            .filter((user) => !user.deletedAt)
            .map((user) => ({
                value: user.email.toLowerCase(),
                label: getFullName(user.firstName, user.lastName),
                subtitle: user.email,
            }));
    }, [platoUsers]);

    return (
        <DialogForm.Item className="items-start gap-1">
            <DialogForm.Label icon={UsersIcon} />
            <DialogForm.Body className="min-w-0 flex-col gap-2">
                <CreatableMultiSelect
                    options={options}
                    value={attendees}
                    onChange={(emails) => {
                        setValue('attendees', emails);
                        setEmailError('');
                    }}
                    placeholder={t('events__form_attendees_placeholder')}
                    disabled={disabled}
                    variant="inline"
                    isValidNewOption={(input) => input.trim().includes('@')}
                    onCreateOption={(input) => input.trim().toLowerCase()}
                    onInvalidOption={() => {
                        setEmailError(t('events__form_attendees_invalid_email'));
                    }}
                    formatOptionLabel={(option) => (
                        <div className="flex flex-col">
                            <Text size="sm" color="neutral-950">
                                {option.label}
                            </Text>
                            <Text size="xs" color="neutral-500">
                                {option.subtitle}
                            </Text>
                        </div>
                    )}
                    errorMessage={emailError}
                />
            </DialogForm.Body>
        </DialogForm.Item>
    );
};
